import * as Sentry from "@sentry/nextjs"
import type { CouponSnapshot } from "@/lib/coupons/validate"
import type { CouponScope } from "@/lib/coupons/engine"

/**
 * Lo MÍNIMO que hace falta para preguntar por el ámbito.
 *
 * Existe porque los cinco rieles no reciben la misma forma: los tres del
 * servidor traen un `CouponSnapshot` completo, pero los dos del navegador
 * reciben objetos parciales — `couponMathOf` (hooks/useCart.ts) arma
 * `{discount_type, discount_value}` y el `AppliedCoupon` del micrositio sale de
 * lo que devuelve el preview. Tipar el predicado contra `CouponSnapshot` obligaba
 * a cada riel del navegador a inventarse su propia copia, y tres copias del mismo
 * predicado es cómo se llega a que arreglen una y las otras dos no.
 */
export interface MaybeScopedCoupon {
  scope?: unknown
  max_scope_units?: unknown
  max_discount_amount?: unknown
}

/**
 * Lleva un `scope` crudo a la forma que el resto del código declara: LAS DOS
 * listas siempre presentes, la clave ausente normalizada a `[]`.
 *
 * 🔴 Existe porque el `CHECK coupons_scope_shape` (migración 20260820174233) y
 * el tipo `CouponScope` NO dicen lo mismo, y la base es la más laxa de las dos:
 * el CHECK acepta explícitamente una fila con UNA sola de las dos claves
 * ("ausente = no participa", su propio comentario), mientras `CouponScope`
 * declara `category_ids` e `item_ids` como `string[]` NO opcionales. Sin esta
 * normalización, una fila que la base BENDICE:
 *
 *   - hace fallar el `safeParse` del body online cuando el zod exige las dos
 *     claves ⇒ `couponSnapshotFromIntentPayload` devuelve `null` ⇒ el route hace
 *     `coupon = anchoredCoupon ?? undefined` y BORRA el cupón: el cliente pagó
 *     con descuento y la orden queda a precio de lista (el mismo mecanismo del
 *     defecto de dinero que cerró el PR del micrositio); y
 *   - hace LANZAR al motor: `lineMatchesScope` hace `scope.item_ids.includes(…)`
 *     sin guarda ⇒ `TypeError: Cannot read properties of undefined`.
 *
 * El arreglo va del lado del CÓDIGO —que acepte lo que la base acepta— y no del
 * `CHECK`, porque endurecer el `CHECK` exige una migración y este PR no lleva
 * ninguna. Aguas abajo nadie tiene que distinguir "ausente" de "vacía".
 *
 * Un `scope` que no es objeto (imposible por el `CHECK`, posible por un jsonb
 * escrito a mano) sale como `{category_ids: [], item_ids: []}` — NUNCA `null`:
 * sigue siendo "trae ámbito" para `snapshotHasScope`, así que los rieles
 * pre-cobro lo siguen rechazando en vez de tratarlo como cupón sin ámbito.
 */
export function normalizeCouponScope(raw: unknown): CouponScope | null {
  if (raw == null) return null
  const r = raw as Partial<CouponScope>
  return {
    category_ids: Array.isArray(r.category_ids) ? r.category_ids : [],
    item_ids: Array.isArray(r.item_ids) ? r.item_ids : [],
  }
}

/**
 * ¿Este cupón trae ámbito o topes? Predicado puro. ÚNICO — lo comparten los
 * cinco rieles (PR-2a Task 5).
 *
 * Los topes cuentan SOLOS: un `max_scope_units` sin `scope` sigue significando
 * "cubre N unidades", y computarlo como si no existiera regala de más.
 *
 * 🔴 `!= null` (laxo), y NO se debe "endurecer" a `!==` creyendo que mejora el
 * tipado — sería un defecto de DINERO, y de los que no salen en los tests sino
 * en producción. `!=` trata `null` y `undefined` igual, que es exactamente lo
 * que hace falta en las cinco superficies:
 *
 *   - Servidor (`validateCouponStrict`, `snapshotFromRow`, el zod del body): las
 *     tres claves SIEMPRE están, en `null` cuando no hay ámbito. Da igual `!=`
 *     que `!==`.
 *   - Navegador (espejo POS y carrito del micrositio): las tres claves NO
 *     EXISTEN en el 100% del tráfico de hoy. Con `!==`, `undefined !== null` es
 *     `true` ⇒ este predicado diría "trae ámbito" de TODOS los cupones vivos y
 *     los rieles pre-cobro los rechazarían a todos.
 *
 * Lo que se cede a cambio, dicho explícitamente: una proyección que se olvide de
 * traer `scope` ya no dispara la alarma — se lee como "no hay ámbito". Ese
 * riesgo se cubre en la frontera, no acá: `snapshotFromRow` (lib/coupons/anchor.ts)
 * y `validateCouponStrict` pasan `row.scope` por `normalizeCouponScope` sobre un
 * SELECT que sí pide la columna, y el zod de `onlineOrderCouponSchema` normaliza
 * con `.nullish().transform(normalizeCouponScope)`.
 */
export function snapshotHasScope(snapshot: MaybeScopedCoupon): boolean {
  return (
    snapshot.scope != null ||
    snapshot.max_scope_units != null ||
    snapshot.max_discount_amount != null
  )
}

/**
 * Avisa que a un riel le llegó un cupón con ámbito que no sabe computar.
 *
 * PR-2a hace que el ámbito VIAJE; PR-2b hará que MANDE. Entre uno y otro, un
 * riel que reciba ámbito y siga computando como antes descontaría sobre la
 * orden ENTERA — con `percent 100` regalaría todo.
 *
 * La barandilla de tipos no alcanza: el `?: never` de `CouponMath` impide pasar
 * el snapshot entero, pero NO ve el destructuring, que es justo lo que hace
 * app/api/orders/route.ts:1016.
 *
 * 🔴 NUNCA lanza. Esta función es para los rieles que corren DESPUÉS del cobro:
 * ahí reventar deja dinero tomado sin orden ni comanda en cocina. Los rieles
 * pre-cobro no la usan — usan `snapshotHasScope` y rechazan, que es más seguro
 * y es lo correcto donde todavía no se movió dinero.
 *
 * La promesa de no-lanzar cubre TAMBIÉN el fallo de la propia telemetría, no
 * solo el camino feliz: si Sentry no está inicializado, el transporte está
 * caído o se agotó la cuota, `captureMessage` puede lanzar — y lo haría
 * exactamente en el riel post-cobro donde la garantía importa. El try/catch de
 * abajo NO es limpieza, es la garantía misma. La telemetría nunca rompe el
 * camino que observa (mismo criterio que `reportUnknown` en
 * lib/print/printerConnection.ts): el catch queda vacío a propósito — si la
 * telemetría falla no hay a quién avisarle, y cualquier cosa que hagas ahí
 * dentro puede lanzar también.
 *
 * Va a Sentry, no a `console.error`: en un route handler el log no lo mira
 * nadie, y en el navegador no sale del dispositivo del cliente.
 */
export function reportScopeUnsupported(snapshot: CouponSnapshot, riel: string): void {
  if (!snapshotHasScope(snapshot)) return
  try {
    Sentry.captureMessage("coupon_scope_unsupported", {
      level: "error",
      tags: { riel },
      extra: {
        coupon_id: snapshot.coupon_id,
        code: snapshot.code,
        scope: snapshot.scope,
        max_scope_units: snapshot.max_scope_units,
        max_discount_amount: snapshot.max_discount_amount,
      },
    })
  } catch {
    /* no-op — la telemetría nunca rompe el camino que observa */
  }
}
